import type React from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import type { WorkoutSessionDisplayData } from '../../types/workoutLapData'
import { formatDurationMin, formatMMSS } from '../../utils/dateUtils'

const MetricsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};

  @media (max-width: 640px) {
    gap: ${theme.spacing.xs};
    grid-template-columns: repeat(2, 50%);
    display: grid;
  }
`

const MetricCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm};
  text-align: center;
  flex: 0 1 auto;
  min-width: 150px;

  @media (max-width: 640px) {
    padding: ${theme.spacing.xs};
    min-width: 120px;
  }
`

const MetricValue = styled.div`
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};

  @media (max-width: 640px) {
    font-size: ${theme.typography.md};
  }
`

const MetricUnit = styled.span`
  padding-left: ${theme.spacing.xs};
`

const MetricLabel = styled.div<{ $hasTooltip?: boolean }>`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  text-overflow: ellipsis;
  position: relative;
  ${({ $hasTooltip }) => $hasTooltip && 'text-decoration: underline dotted;'}
  ${({ $hasTooltip }) => $hasTooltip && 'cursor: help;'}
`

const Tooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(8px);
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm};
  font-size: ${theme.typography.xs};
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text};
  white-space: normal;
  max-width: 250px;
  width: max-content;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 1000;
  box-shadow: ${({ theme }) => theme.shadows.md};
  line-height: 1.4;

  ${MetricLabel}:hover & {
    opacity: 0.85;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-bottom-color: ${({ theme }) => theme.colors.border};
  }

  @media (max-width: 640px) {
    max-width: 200px;
    font-size: ${theme.typography.xs};
  }
`

const TooltipTitle = styled.div`
  font-weight: 600;
  margin-bottom: ${theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text};
`

const TooltipText = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
`

const SkeletonWrapper = styled.div`
  .react-loading-skeleton {
    --base-color: ${({ theme }) => theme.colors.surface};
    --highlight-color: ${({ theme }) => theme.colors.border};
    border-radius: ${theme.borderRadius.md};
  }
`

interface MetricTooltip {
  title: string
  description: string
}

interface MetricConfig {
  key: string
  value: string | number | null | undefined
  unit: string | null
  label: string
  condition: boolean
  tooltip?: MetricTooltip
}

interface WorkoutKeyMetricsProps {
  activityType?: string | null
  workoutSession?: WorkoutSessionDisplayData | undefined
  avgStrokesPerLength?: number | null
  avgPace?: number
  idleTime?: number
  avgSpeed?: number
  calculatedMetrics?: {
    movingTimeSec?: number
    restTimeSec?: number
  } | null
  isLoading?: boolean
  metricTooltips?: Record<string, MetricTooltip>
}

export const WorkoutKeyMetrics: React.FC<WorkoutKeyMetricsProps> = ({
  activityType,
  workoutSession,
  avgStrokesPerLength,
  avgPace = 0,
  idleTime,
  avgSpeed = 0,
  calculatedMetrics,
  isLoading = false,
  metricTooltips = {},
}) => {
  const isRunning = activityType?.includes('running')
  const isCycling = activityType?.includes('cycling')
  const isSwimming = activityType?.includes('swimming')

  const totalCalories = workoutSession?.total_calories ?? 0
  const avgPower = workoutSession?.average_power_watts
  const normalizedPower = workoutSession?.normalized_power_watts
  const avgHeartRate = workoutSession?.average_heart_rate_bpm ?? 0
  const totalDistance = workoutSession?.total_distance ?? 0

  // Define metrics configuration for each sport
  const metrics: MetricConfig[] = []

  // Common metrics for running and cycling
  if (isRunning || isCycling) {
    metrics.push(
      {
        key: 'duration',
        value:
          workoutSession?.total_timer_time !== null && workoutSession?.total_timer_time !== undefined
            ? formatDurationMin(workoutSession.total_timer_time / 60)
            : null,
        unit: null,
        label: 'Duration',
        condition: workoutSession?.total_timer_time !== null && workoutSession?.total_timer_time !== undefined,
      },
      {
        key: 'distance',
        value:
          workoutSession?.total_distance !== null && workoutSession?.total_distance !== undefined
            ? Math.floor(workoutSession.total_distance / 1000).toFixed(1)
            : null,
        unit: 'km',
        label: 'Distance',
        condition: workoutSession?.total_distance !== null && workoutSession?.total_distance !== undefined,
      },
      {
        key: 'totalCalories',
        value: totalCalories,
        unit: 'Kcal',
        label: 'Total Energy',
        condition: totalCalories > 0,
        tooltip: metricTooltips.totalEnergy,
      },
      {
        key: 'avgPower',
        value: avgPower,
        unit: 'W',
        label: 'Avg Power',
        condition: Boolean(avgPower && avgPower > 0),
        tooltip: metricTooltips.avgPower,
      },
      {
        key: 'normalizedPower',
        value: normalizedPower,
        unit: 'W',
        label: 'Adjusted Power',
        condition: Boolean(normalizedPower && normalizedPower > 0),
        tooltip: metricTooltips.normalizedPower,
      }
    )
  }

  // Cycling-specific idle time
  /* not active yet, it is loaded async and causes layout shift
  if (isCycling) {
    metrics.push({
      key: 'idleTime',
      value: idleTime !== undefined && idleTime > 0 ? formatDurationMin(idleTime / 60) : null,
      unit: null,
      label: 'Idle Time',
      condition: idleTime !== undefined && idleTime > 0,
      tooltip: metricTooltips.idleTime,
    })
  }
   */

  // Speed/pace metrics
  if (isRunning) {
    metrics.push({
      key: 'avgPace',
      value: avgPace > 0 ? formatMMSS(avgPace * 60) : null,
      unit: 'min/km',
      label: 'Avg Pace',
      condition: avgPace > 0,
    })
  }

  if (isCycling) {
    metrics.push({
      key: 'avgSpeed',
      value: avgSpeed > 0 ? avgSpeed.toFixed(1) : null,
      unit: 'km/h',
      label: 'Avg Speed',
      condition: avgSpeed > 0,
    })
  }

  // Swimming-specific metrics
  if (isSwimming) {
    metrics.push(
      {
        key: 'totalDistance',
        value: totalDistance > 0 ? Math.round(totalDistance) : null,
        unit: 'm',
        label: 'Total Distance',
        condition: totalDistance > 0,
      },
      {
        key: 'avgStrokesPerLength',
        value: workoutSession?.avg_stroke_count ?? avgStrokesPerLength ?? null,
        unit: null,
        label: 'Strokes / Length',
        condition: avgStrokesPerLength !== null && avgStrokesPerLength !== undefined && avgStrokesPerLength > 0,
        tooltip: metricTooltips.strokesPerLength,
      },
      {
        key: 'avgStrokeDistance',
        value:
          workoutSession?.avg_stroke_distance !== null &&
          workoutSession?.avg_stroke_distance !== undefined &&
          workoutSession?.avg_stroke_distance > 0
            ? workoutSession.avg_stroke_distance.toFixed(2)
            : null,
        unit: 'm',
        label: 'Avg Distance/Stroke',
        condition:
          workoutSession?.avg_stroke_distance !== null &&
          workoutSession?.avg_stroke_distance !== undefined &&
          workoutSession?.avg_stroke_distance > 0,
      },
      {
        key: 'movingTime',
        value:
          calculatedMetrics?.movingTimeSec && calculatedMetrics.movingTimeSec > 0
            ? formatDurationMin(calculatedMetrics.movingTimeSec / 60)
            : null,
        unit: null,
        label: 'Moving Time',
        condition: Boolean(calculatedMetrics?.movingTimeSec && calculatedMetrics.movingTimeSec > 0),
        tooltip: metricTooltips.movingTime,
      },
      {
        key: 'restTime',
        value:
          calculatedMetrics?.restTimeSec && calculatedMetrics.restTimeSec > 0
            ? formatDurationMin(calculatedMetrics.restTimeSec / 60)
            : null,
        unit: null,
        label: 'Rest Time',
        condition: Boolean(calculatedMetrics?.restTimeSec && calculatedMetrics.restTimeSec > 0),
        tooltip: metricTooltips.restTime,
      }
    )
  }

  // Common metrics for all sports - Heart Rate
  if ((isRunning || isCycling || isSwimming) && avgHeartRate > 0) {
    metrics.push({
      key: 'avgHeartRate',
      value: avgHeartRate,
      unit: 'bpm',
      label: 'Avg Heart Rate',
      condition: true,
    })
  }

  // Cadence for running
  if (isRunning && workoutSession?.average_cadence_spm) {
    metrics.push({
      key: 'stepFrequency',
      value: workoutSession.average_cadence_spm,
      unit: 'steps/min',
      label: 'Step Frequency',
      condition: true,
    })
  }

  // Core temperature for running and cycling
  if ((isRunning || isCycling) && workoutSession?.average_core_temperature_celsius) {
    metrics.push({
      key: 'avgCoreTemp',
      value: workoutSession.average_core_temperature_celsius.toFixed(1),
      unit: '°C',
      label: 'Avg Core Temp',
      condition: true,
    })
  }

  // Calories for swimming (shown at end)
  if (isSwimming && totalCalories > 0) {
    metrics.push({
      key: 'totalCalories',
      value: totalCalories,
      unit: 'Kcal',
      label: 'Total Calories',
      condition: true,
      tooltip: metricTooltips.totalEnergy,
    })
  }

  return (
    <MetricsGrid>
      {metrics
        .filter((metric) => metric.condition)
        .map((metric) => (
          <MetricCard key={metric.key}>
            {!workoutSession ? (
              <SkeletonWrapper>
                <Skeleton height={24} width="60%" style={{ marginBottom: 4 }} />
                <Skeleton height={14} width="80%" />
              </SkeletonWrapper>
            ) : (
              <>
                <MetricValue>
                  {metric.value}
                  <MetricUnit>{metric.unit}</MetricUnit>
                </MetricValue>
                <MetricLabel $hasTooltip={!!metric.tooltip}>
                  {metric.label}
                  {metric.tooltip && (
                    <Tooltip>
                      <TooltipTitle>{metric.tooltip.title}</TooltipTitle>
                      <TooltipText>{metric.tooltip.description}</TooltipText>
                    </Tooltip>
                  )}
                </MetricLabel>
              </>
            )}
          </MetricCard>
        ))}
    </MetricsGrid>
  )
}
